import React, { useState, useCallback, useMemo, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  LinearProgress,
  Alert,
  Stack,
  Step,
  StepLabel,
  Stepper,
  MenuItem,
  IconButton,
} from "@mui/material"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty"
import ThumbUpIcon from "@mui/icons-material/ThumbUp"
import ThumbDownIcon from "@mui/icons-material/ThumbDown"
import AddIcon from "@mui/icons-material/Add"
import RemoveIcon from "@mui/icons-material/Remove"
import { uploadAgentFile } from "../../../services/aiAgent"
import { checkUniqueStationName } from "../../../services/workstation"
import { checkUniqueUsecaseName } from "../../../services/usecase"
import { checkUniquePartName } from "../../../services/part"
import { useDispatch, useSelector } from "react-redux"
import {
  getWorkstationData,
  getSelectedUsecase,
  setWorkstationType,
  setSelectedUsecase,
  setLocalSelectedPart,
  setDataTab,
} from "../../../helpers/storage"
import { convertUTCDateToLocalDate } from "../../../helpers/utils"
import { setSelectedPart, setCallSidebarAPI } from "../../../store/actions"
import { getCopilotUiContext } from "../context/copilotPageContext"
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ScatterChart,
  Scatter,
} from "recharts"

// wait, let's use the correct relative path
import LivisHeatmapHighcharts from "../../GraphNew/charts/LivisHeatmapHighcharts"
import LivisGaugeHighcharts from "../../GraphNew/charts/LivisGaugeHighcharts"
import LivisHorizontalStackedBarHighcharts from "../../GraphNew/charts/LivisHorizontalStackedBarHighcharts"
import LivisComposedChart from "../../GraphNew/charts/LivisComposedChart"
import LivisParetoHighchartsChart from "../../GraphNew/charts/LivisParetoHighcharts"

import LivisAccuracyComparisonChart from "../../GraphNew/charts/LivisAccuracyComparisonChart"
import LivisAreaHighcharts from "../../GraphNew/charts/LivisAreaHighcharts"
import LivisAreaLineChart from "../../GraphNew/charts/LivisAreaLineChart"
import LivisAreaRangeChart from "../../GraphNew/charts/LivisAreaRangeChart"
import LivisBarChart from "../../GraphNew/charts/LivisBarChart"
import LivisBarHighcharts from "../../GraphNew/charts/LivisBarHighcharts"
import LivisBarVerticalChart from "../../GraphNew/charts/LivisBarVerticalChart"
import LivisColumnHighcharts from "../../GraphNew/charts/LivisColumnHighcharts"
import LivisDonutChart from "../../GraphNew/charts/LivisDonutChart"
import LivisDonutProgressChart from "../../GraphNew/charts/LivisDonutProgressChart"
import LivisGaugeChart from "../../GraphNew/charts/LivisGaugeChart"
import LivisGaugeSegmentedChart from "../../GraphNew/charts/LivisGaugeSegmentedChart"
import LivisGradientBarChart from "../../GraphNew/charts/LivisGradientBarChart"
import LivisHorizontalBarHighcharts from "../../GraphNew/charts/LivisHorizontalBarHighcharts"
import LivisPieDonutChart from "../../GraphNew/charts/LivisPieDonutChart"
import LivisPieHighcharts from "../../GraphNew/charts/LivisPieHighcharts"
import LivisPillBarChart from "../../GraphNew/charts/LivisPillBarChart"
import LivisProgressHighcharts from "../../GraphNew/charts/LivisProgressHighcharts"
import LivisSplineChart from "../../GraphNew/charts/LivisSplineChart"
import LivisStackedBarHighcharts from "../../GraphNew/charts/LivisStackedBarHighcharts"

export interface MarkerProps {
  id: string
  payload: any
  sessionId: string
  disabled?: boolean
  onAction: (markerId: string, action: string, value: any) => void
  onUploadComplete?: (fileRef: string, kind: string, context: any) => void
}

const cardSx = {
  mb: 1,
  borderRadius: 2,
  
  background: "var(--livis-color-background-white, var(--livis-color-background-white))",
}
const primaryBtnProps = {
  variant: "outlined" as const,
  className: "livis-primary-btn",
  size: "small" as const,
}
const secondaryBtnProps = {
  variant: "outlined" as const,
  className: "livis-secondary-btn",
  size: "small" as const,
}
const textFieldClass = "livis-input-field"

export function applyTerminology(text: string | undefined): string {
  if (!text) return ""
  let str = text
  str = str.replace(/\bUsecase\b/g, "AI Inspector")
  str = str.replace(/\busecase\b/g, "AI inspector")
  str = str.replace(/\bUsecases\b/g, "AI Inspectors")
  str = str.replace(/\busecases\b/g, "AI inspectors")
  str = str.replace(/\bWorkstation\b/g, "Inspection Station")
  str = str.replace(/\bworkstation\b/g, "inspection station")
  str = str.replace(/\bWorkstations\b/g, "Inspection Stations")
  str = str.replace(/\bworkstations\b/g, "inspection stations")
  str = str.replace(/(?<!Inspection\s)\bStation\b/g, "Inspection Station")
  str = str.replace(/(?<!inspection\s)\bstation\b/g, "inspection station")
  str = str.replace(/(?<!Inspection\s)\bStations\b/g, "Inspection Stations")
  str = str.replace(/(?<!inspection\s)\bstations\b/g, "inspection stations")
  return str
}

function renderMd(text: string): string {
  text = applyTerminology(text || "")
  const safe = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")

  const processInline = (s: string) =>
    s
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, "<em>$1</em>")
      .replace(/_(.*?)_/g, "<em>$1</em>")
      .replace(
        /`([^`]+)`/g,
        '<code style="background:var(--livis-color-background, var(--livis-canvas-grid-line-color));padding:1px 4px;border-radius:3px;font-size:0.85em">$1</code>'
      )

  const lines = safe.split("\n")
  const out: string[] = []
  let inList = false

  for (const raw of lines) {
    const line = raw.trim()
    if (!line) {
      if (inList) {
        out.push("</ul>")
        inList = false
      }
      out.push("<br/>")
      continue
    }
    const hm = line.match(/^(#{1,4})\s+(.*)/)
    if (hm) {
      if (inList) {
        out.push("</ul>")
        inList = false
      }
      const lvl = Math.min(hm[1].length + 3, 6)
      out.push(
        `<h${lvl} style="margin:4px 0;font-weight:600">${processInline(
          hm[2]
        )}</h${lvl}>`
      )
      continue
    }
    const lm = line.match(/^[-*]\s+(.*)/)
    if (lm) {
      if (!inList) {
        out.push('<ul style="margin:4px 0;padding-left:1.2em">')
        inList = true
      }
      out.push(`<li>${processInline(lm[1])}</li>`)
      continue
    }
    if (inList) {
      out.push("</ul>")
      inList = false
    }
    out.push(`<span style="display:block">${processInline(line)}</span>`)
  }
  if (inList) out.push("</ul>")
  return out.join("")
}

const StatusIcon: React.FC<{ state: string }> = ({ state }) => {
  if (state === "done")
    return (
      <CheckCircleIcon
        fontSize="small"
        style={{ color: "var(--livis-labelled-color, var(--livis-labelled-color))" }}
      />
    )
  if (state === "failed")
    return (
      <ErrorOutlineIcon
        fontSize="small"
        style={{ color: "var(--livis-color-secondary-red, var(--livis-color-secondary-red))" }}
      />
    )
  if (state === "queued")
    return (
      <HourglassEmptyIcon
        fontSize="small"
        style={{ color: "var(--livis-gray-font-color, var(--livis-gray-font-color))" }}
      />
    )
  return (
    <CircularProgress
      size={16}
      sx={{ color: "var(--livis-color-primary-blue, var(--livis-color-primary-blue))" }}
    />
  )
}

const MarkerCard: React.FC<{ children: React.ReactNode; title?: string }> = ({
  children,
  title,
}) => (
  <Card
    variant="outlined"
    sx={cardSx}
    className="livis-ai-assistant-marker-card"
  >
    <CardContent>
      {title && (
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
      )}
      {children}
    </CardContent>
  </Card>
)

const readChooseSubmission = (payload: any, isMulti: boolean) => {
  const raw =
    payload?.submitted_value ??
    payload?.selected_value ??
    payload?.submitted_values
  if (isMulti) {
    return Array.isArray(raw) ? raw.map(String) : []
  }
  if (raw === undefined || raw === null || raw === "") return ""
  return String(raw)
}

const ChooseMarker: React.FC<MarkerProps> = ({
  id,
  payload,
  disabled,
  onAction,
}) => {
  const isMulti = payload?.kind === "multi"
  const [single, setSingle] = useState<string>(
    () => readChooseSubmission(payload, false) as string
  )
  const [multi, setMulti] = useState<string[]>(
    () => readChooseSubmission(payload, true) as string[]
  )
  useEffect(() => {
    if (isMulti) {
      setMulti(readChooseSubmission(payload, true) as string[])
    } else {
      setSingle(readChooseSubmission(payload, false) as string)
    }
  }, [
    isMulti,
    payload?.submitted_value,
    payload?.selected_value,
    payload?.submitted_values,
  ])
  const toggle = (v: string) =>
    setMulti((m) => (m.includes(v) ? m.filter((x) => x !== v) : [...m, v]))
  const submit = () => onAction(id, "select", isMulti ? multi : single)
  return (
    <MarkerCard title={applyTerminology(payload?.label)}>
      {isMulti ? (
        <Stack>
          {(payload?.options || []).map((o: any) => (
            <FormControlLabel
              key={o.value}
              control={
                <Checkbox
                  checked={multi.includes(o.value)}
                  onChange={() => toggle(o.value)}
                  disabled={disabled}
                />
              }
              label={
                <span>
                  {applyTerminology(o.label)}
                  {o.recommended ? (
                    <Chip size="small" label="recommended" sx={{ ml: 1 }} />
                  ) : null}
                  {o.description ? (
                    <Typography variant="caption" display="block">
                      {applyTerminology(o.description)}
                    </Typography>
                  ) : null}
                </span>
              }
            />
          ))}
        </Stack>
      ) : (
        <RadioGroup value={single} onChange={(_, v) => setSingle(v)}>
          {(payload?.options || []).map((o: any) => (
            <FormControlLabel
              key={o.value}
              value={o.value}
              control={<Radio disabled={disabled} />}
              label={
                <span>
                  {applyTerminology(o.label)}
                  {o.recommended ? (
                    <Chip size="small" label="recommended" sx={{ ml: 1 }} />
                  ) : null}
                  {o.description ? (
                    <Typography variant="caption" display="block">
                      {applyTerminology(o.description)}
                    </Typography>
                  ) : null}
                </span>
              }
            />
          ))}
        </RadioGroup>
      )}
      <Button
        {...primaryBtnProps}
        onClick={submit}
        disabled={disabled || (isMulti ? !multi.length : !single)}
        sx={{ mt: 1 }}
      >
        {applyTerminology(payload?.submit_label || "Submit")}
      </Button>
    </MarkerCard>
  )
}

const InputMarker: React.FC<MarkerProps> = ({
  id,
  payload,
  disabled,
  onAction,
}) => {
  const [val, setVal] = useState<string>(
    () =>
      (payload?.submitted_value != null
        ? String(payload.submitted_value)
        : payload?.default) ?? ""
  )
  useEffect(() => {
    if (payload?.submitted_value != null) {
      setVal(String(payload.submitted_value))
    }
  }, [payload?.submitted_value])
  const submit = () => onAction(id, "submit", val)
  return (
    <MarkerCard title={applyTerminology(payload?.label)}>
      <TextField
        fullWidth
        size="small"
        className={textFieldClass}
        type={payload?.kind === "number" ? "number" : "text"}
        multiline={payload?.kind === "textarea"}
        rows={payload?.kind === "textarea" ? 3 : 1}
        placeholder={applyTerminology(payload?.placeholder || "")}
        value={val}
        disabled={disabled}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault()
            submit()
          }
        }}
      />
      <Button
        {...primaryBtnProps}
        onClick={submit}
        disabled={disabled || (payload?.required && !val)}
        sx={{ mt: 1 }}
      >
        Submit
      </Button>
    </MarkerCard>
  )
}

// ---- Field validation helpers ----
// Regex compilation is expensive. Cache by pattern string — avoids creating a new
// RegExp on every keystroke for every field in every repeating row.
const _rxCache: Map<string, RegExp> = new Map()
function getRx(pattern: string): RegExp {
  let rx = _rxCache.get(pattern)
  if (!rx) {
    rx = new RegExp(pattern)
    _rxCache.set(pattern, rx)
  }
  return rx
}

function validateField(
  field: any,
  value: any,
  allValues?: Record<string, any>
): string {
  if (!field) return ""
  if (
    field.required &&
    (value === undefined ||
      value === null ||
      value === "" ||
      (Array.isArray(value) && !value.length))
  ) {
    return "required"
  }
  const sv = typeof value === "string" ? value : ""
  if (field.min_len && sv.length && sv.length < field.min_len)
    return `min ${field.min_len} chars`
  if (field.max_len && sv.length > field.max_len)
    return `max ${field.max_len} chars`
  if (field.pattern && sv && !getRx(field.pattern).test(sv))
    return "invalid format"
  if (field.depends_on && field.address_rules && allValues) {
    const dep = allValues[field.depends_on]
    const rule = field.address_rules[dep] || field.address_rules.default
    if (rule?.pattern && sv && !getRx(rule.pattern).test(sv))
      return rule.placeholder
        ? `expected: ${rule.placeholder}`
        : "invalid format"
  }
  return ""
}

function extractUniqueCheckError(res: any): string {
  if (!res) return ""
  if (res.status === 200) return ""
  const data = res.data
  if (typeof data === "string") {
    const t = data.trim()
    if (t && !/^\d+$/.test(t)) return t
  }
  const candidates = [
    data?.message,
    data?.detail,
    data?.error,
    typeof data?.data === "object" ? data?.data?.message : undefined,
  ]
  for (const c of candidates) {
    if (typeof c === "string") {
      const t = c.trim()
      if (t && !/^\d+$/.test(t)) return t
    }
  }
  return "This name is already taken"
}

type UniqueFormContext = {
  workstation_id?: string
  usecase_id?: string
}

function resolveUniqueFormContext(
  formContext?: Record<string, unknown>
): UniqueFormContext {
  const ctx = getCopilotUiContext()
  const ws = getWorkstationData() as Record<string, unknown> | null
  const uc = getSelectedUsecase() as Record<string, unknown> | null
  const workstation_id = String(
    formContext?.workstation_id ||
      ctx.workstation_id ||
      ws?._id ||
      ws?.id ||
      ws?.workstation_id ||
      ""
  ).trim()
  const usecase_id = String(
    formContext?.usecase_id ||
      ctx.usecase_id ||
      uc?._id ||
      uc?.id ||
      uc?.usecase_id ||
      ""
  ).trim()
  return { workstation_id, usecase_id }
}

function useDebouncedUniqueCheck(
  field: any,
  value: string,
  formContext?: Record<string, unknown>,
  enabled = true
): string {
  const [uniqueErr, setUniqueErr] = useState("")
  useEffect(() => {
    if (!enabled) {
      setUniqueErr("")
      return
    }
    const kind = field?.check_unique
    const sv = typeof value === "string" ? value.trim() : ""
    if (!kind || sv.length < 3) {
      setUniqueErr("")
      return
    }
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const ids = resolveUniqueFormContext(formContext)
        if (kind === "workstation") {
          const res = await checkUniqueStationName({ workstation_name: sv })
          if (!cancelled) setUniqueErr(extractUniqueCheckError(res))
        } else if (kind === "usecase") {
          if (!ids.workstation_id) {
            if (!cancelled) setUniqueErr("")
            return
          }
          const res = await checkUniqueUsecaseName({
            usecase_name: sv,
            workstation_id: ids.workstation_id,
          })
          if (!cancelled) setUniqueErr(extractUniqueCheckError(res))
        } else if (kind === "part_name") {
          if (!ids.usecase_id) {
            if (!cancelled) setUniqueErr("")
            return
          }
          const res = await checkUniquePartName({
            part_name: sv,
            usecase_id: ids.usecase_id,
          })
          if (!cancelled) setUniqueErr(extractUniqueCheckError(res))
        }
      } catch {
        if (!cancelled) setUniqueErr("")
      }
    }, 500)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [field?.check_unique, value, formContext, enabled])
  return uniqueErr
}

/** Strip leading "+" when button already uses MUI AddIcon. */
function addButtonLabel(raw: string | undefined, fallback: string): string {
  const text = applyTerminology(raw || fallback)
  return text.replace(/^\+\s*/, "")
}

function getAddressPlaceholder(
  field: any,
  allValues: Record<string, any>
): string {
  if (!field?.depends_on || !field?.address_rules)
    return field?.placeholder || ""
  const dep = allValues[field.depends_on]
  const rule = field.address_rules[dep] || field.address_rules.default
  return rule?.placeholder || rule?.default || field?.placeholder || ""
}

// ---- Single field renderer (used by Form and by row inside RepeatingBlock) ----
const FieldRenderer: React.FC<{
  field: any
  value: any
  allValues: Record<string, any>
  onChange: (v: any) => void
  disabled?: boolean
  errorOverride?: string
  formContext?: Record<string, unknown>
  onUniqueError?: (fieldId: string, message: string) => void
}> = ({
  field,
  value,
  allValues,
  onChange,
  disabled,
  errorOverride,
  formContext,
  onUniqueError,
}) => {
  const uniqueEnabled = !disabled
  const uniqueErr = useDebouncedUniqueCheck(
    field,
    value ?? "",
    formContext,
    uniqueEnabled
  )
  const baseErr = disabled
    ? ""
    : errorOverride ?? validateField(field, value, allValues)
  const serverHelper =
    !disabled && !errorOverride && !baseErr && field?.helper_text
      ? String(field.helper_text)
      : ""
  const err = uniqueErr || baseErr || serverHelper

  useEffect(() => {
    if (!uniqueEnabled) {
      onUniqueError?.(field.id, "")
      return
    }
    onUniqueError?.(field.id, uniqueErr)
  }, [field.id, uniqueErr, onUniqueError, uniqueEnabled])

  if (field.kind === "select") {
    return (
      <TextField
        select
        variant="standard"
        fullWidth
        size="small"
        error={!!err}
        helperText={err || ""}
        label={applyTerminology(field.label)}
        value={value ?? field.initial_value ?? field.default ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || field.disabled}
        className={textFieldClass}
        SelectProps={{
          MenuProps: {
            container: () => document.body,
            sx: { zIndex: 9999999 },
            PaperProps: {
              sx: {
                zIndex: 9999999,
                maxHeight: 320,
                background: "var(--livis-color-background-white, var(--livis-color-background-white))",
                color: "var(--livis-black-font-color, var(--livis-black-font-color))",
              },
            },
            keepMounted: true,
          },
        }}
      >
        {(field.options || []).map((o: any) => (
          <MenuItem
            key={o.value}
            value={o.value}
            sx={{ color: "var(--livis-black-font-color, var(--livis-black-font-color))" }}
          >
            {applyTerminology(o.label)}
          </MenuItem>
        ))}
      </TextField>
    )
  }
  if (field.kind === "multiselect") {
    const v: string[] = Array.isArray(value) ? value : field.default || []
    return (
      <TextField
        select
        variant="standard"
        fullWidth
        size="small"
        error={!!err}
        helperText={err || ""}
        label={applyTerminology(field.label)}
        value={v}
        onChange={(e) =>
          onChange(
            typeof e.target.value === "string"
              ? e.target.value.split(",")
              : e.target.value
          )
        }
        disabled={disabled}
        className={textFieldClass}
        SelectProps={{
          multiple: true,
          renderValue: (sel: any) => (
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              {(sel as string[]).map((s) => (
                <Chip key={s} label={s.replace(/_/g, " ")} size="small" />
              ))}
            </Box>
          ),
          MenuProps: {
            container: () => document.body,
            sx: { zIndex: 9999999 },
            PaperProps: {
              sx: {
                zIndex: 9999999,
                maxHeight: 320,
                background: "var(--livis-color-background-white, var(--livis-color-background-white))",
                color: "var(--livis-black-font-color, var(--livis-black-font-color))",
              },
            },
            keepMounted: true,
          },
        }}
      >
        {(field.options || []).map((o: any) => (
          <MenuItem
            key={o.value}
            value={o.value}
            sx={{ color: "var(--livis-black-font-color, var(--livis-black-font-color))" }}
          >
            {applyTerminology(o.label)}
          </MenuItem>
        ))}
        {field.allow_create && (
          <MenuItem
            value="__add_new__"
            sx={{ color: "var(--livis-black-font-color, var(--livis-black-font-color))" }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              const name = prompt(`Add a new ${field.label.toLowerCase()}:`)
              if (name && name.trim()) {
                const slug = name.trim().toLowerCase().replace(/\s+/g, "_")
                if (!v.includes(slug)) onChange([...v, slug])
              }
            }}
          >
            + Add new
          </MenuItem>
        )}
      </TextField>
    )
  }
  // text / number / textarea
  const placeholder =
    field.id === "camera_address" ||
    field.id === "device_address" ||
    field.id === "interface_address"
      ? getAddressPlaceholder(field, allValues)
      : field.placeholder || ""
  return (
    <TextField
      label={applyTerminology(field.label)}
      size="small"
      fullWidth
      className={textFieldClass}
      type={field.kind === "number" ? "number" : "text"}
      multiline={field.kind === "textarea"}
      rows={field.kind === "textarea" ? 3 : 1}
      placeholder={applyTerminology(placeholder)}
      value={value ?? field.initial_value ?? field.default ?? ""}
      onChange={(e) =>
        onChange(
          field.kind === "number"
            ? e.target.value === ""
              ? ""
              : Number(e.target.value)
            : e.target.value
        )
      }
      disabled={disabled || field.disabled}
      variant="standard"
      error={!!err}
      helperText={err || ""}
      inputProps={{ maxLength: field.max_len, min: field.min, max: field.max }}
    />
  )
}

// ---- Repeating block: list of rows, each is a sub-form ----
const RepeatingBlock: React.FC<{
  field: any
  rows: Record<string, any>[]
  setRows: (r: Record<string, any>[]) => void
  disabled?: boolean
}> = ({ field, rows, setRows, disabled }) => {
  const tmpl = field.row_template || { fields: [] }
  const min = field.min_rows ?? 0
  const max = field.max_rows ?? 16
  const blankRow = useMemo(() => {
    const out: Record<string, any> = {}
    ;(tmpl.fields || []).forEach((f: any) => {
      out[f.id] = f.default ?? ""
    })
    return out
  }, [tmpl])

  const addRow = () => setRows([...rows, { ...blankRow }])
  const removeRow = (i: number) => setRows(rows.filter((_, j) => j !== i))
  const updateRow = (i: number, fid: string, v: any) => {
    setRows(
      rows.map((r, j) => {
        if (j !== i) return r
        const nr = { ...r, [fid]: v }
        const fld = (tmpl.fields || []).find((f: any) => f.id === fid)
        ;(fld?.triggers_revalidation_of || []).forEach((sib: string) => {
          if (nr[sib] === undefined) nr[sib] = ""
        })
        return nr
      })
    )
  }

  // duplicate detection on row_unique fields
  const uniqueFields: string[] = tmpl.row_unique || []
  const dupes: Record<number, Record<string, boolean>> = {}
  uniqueFields.forEach((uf) => {
    const seen: Record<string, number[]> = {}
    rows.forEach((r, i) => {
      const v = r[uf]
      if (!v) return
      seen[v] = seen[v] || []
      seen[v].push(i)
    })
    Object.values(seen).forEach((idxs) => {
      if (idxs.length > 1)
        idxs.forEach((i) => {
          dupes[i] = { ...(dupes[i] || {}), [uf]: true }
        })
    })
  })

  return (
    <Box
      className="livis-ai-assistant-repeating-block"
      sx={{
        border: "1px solid var(--livis-border-color, var(--livis-border-color))",
        borderRadius: 1,
        p: 1.5,
        mb: 1,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {applyTerminology(field.label)}
      </Typography>
      <Stack spacing={1.5}>
        {rows.map((row, i) => (
          <Card
            key={i}
            variant="outlined"
            sx={{
              p: 1.25,
              background: "var(--livis-color-background-white, var(--livis-color-background-white))",
            }}
          >
            <Stack spacing={1.25}>
              {(tmpl.fields || []).map((f: any) => (
                <FieldRenderer
                  key={f.id}
                  field={f}
                  value={row[f.id]}
                  allValues={row}
                  onChange={(v) => updateRow(i, f.id, v)}
                  disabled={disabled}
                  errorOverride={
                    dupes[i]?.[f.id] ? "must be unique" : undefined
                  }
                />
              ))}
            </Stack>
            {rows.length > min && (
              <Box sx={{ textAlign: "right", mt: 0.5 }}>
                <Button
                  size="small"
                  color="error"
                  onClick={() => removeRow(i)}
                  disabled={disabled}
                >
                  {field.remove_label || "Remove"}
                </Button>
              </Box>
            )}
          </Card>
        ))}
        {rows.length < max && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addRow}
            disabled={disabled}
            variant="outlined"
            className="livis-secondary-btn"
          >
            {addButtonLabel(field.add_label, field.label)}
          </Button>
        )}
      </Stack>
    </Box>
  )
}

const FormMarker: React.FC<MarkerProps> = ({
  id,
  payload,
  disabled,
  onAction,
}) => {
  const fields: any[] = payload?.fields || []
  const stepInfo = payload?.step_info

  // Initial values: prefer payload.initial_values (server resend on validation error), then field.default, then field.initial_rows
  const initial = useMemo(() => {
    const out: Record<string, any> = {}
    fields.forEach((f) => {
      if (f.kind === "repeating_input") {
        out[f.id] =
          payload?.initial_values?.[f.id] ??
          (f.initial_rows && f.initial_rows.length ? f.initial_rows : [])
        if (!out[f.id].length && (f.min_rows ?? 1) > 0) {
          const blank: Record<string, any> = {}
          ;(f.row_template?.fields || []).forEach((sf: any) => {
            blank[sf.id] = sf.default ?? ""
          })
          out[f.id] = [blank]
        }
      } else {
        out[f.id] =
          payload?.initial_values?.[f.id] ??
          f.initial_value ??
          f.default ??
          (f.kind === "multiselect" ? [] : "")
      }
    })
    if (
      payload?.submitted_values &&
      typeof payload.submitted_values === "object"
    ) {
      Object.assign(out, payload.submitted_values)
    }
    return out
  }, [fields, payload?.initial_values, payload?.submitted_values])

  const [values, setValues] = useState<Record<string, any>>(initial)
  useEffect(() => {
    if (
      payload?.submitted_values &&
      typeof payload.submitted_values === "object"
    ) {
      setValues((prev) => ({ ...prev, ...payload.submitted_values }))
    }
  }, [payload?.submitted_values])
  const setField = (fid: string, v: any) =>
    setValues((prev) => ({ ...prev, [fid]: v }))

  const serverErrors: Record<string, string> = payload?.errors || {}
  const [uniqueErrors, setUniqueErrors] = useState<Record<string, string>>({})
  const onUniqueError = useCallback((fieldId: string, message: string) => {
    setUniqueErrors((prev) => {
      if (!message && !prev[fieldId]) return prev
      if (message === prev[fieldId]) return prev
      const next = { ...prev }
      if (message) next[fieldId] = message
      else delete next[fieldId]
      return next
    })
  }, [])

  const fieldHasError = useCallback(
    (f: any, val: any, rowValues?: Record<string, any>) => {
      if (disabled) return false
      const ctx = rowValues ?? values
      return !!(
        validateField(f, val, ctx) ||
        serverErrors[f.id] ||
        uniqueErrors[f.id] ||
        (!serverErrors[f.id] && f.helper_text)
      )
    },
    [values, serverErrors, uniqueErrors, disabled]
  )

  const hasClientError = useMemo(() => {
    for (const f of fields) {
      if (f.kind === "repeating_input") {
        const rows = values[f.id] || []
        if (rows.length < (f.min_rows ?? 0)) return true
        for (const row of rows) {
          for (const sub of f.row_template?.fields || []) {
            if (fieldHasError(sub, row[sub.id], row)) return true
          }
        }
      } else if (fieldHasError(f, values[f.id])) {
        return true
      }
    }
    return false
  }, [fields, values, fieldHasError])

  const submit = () => onAction(id, "submit", values)

  return (
    <MarkerCard title={stepInfo ? undefined : applyTerminology(payload?.title)}>
      {stepInfo && stepInfo.steps?.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {payload?.title && (
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              {payload.title}
            </Typography>
          )}
          <Stepper
            activeStep={stepInfo.active ?? 0}
            alternativeLabel
            sx={{ mb: 1 }}
            className="livis-stepper"
          >
            {(stepInfo.steps as string[]).map((s, i) => (
              <Step key={i}>
                <StepLabel>{s}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}
      {Object.keys(serverErrors).length > 0 && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          Please fix the highlighted fields.
        </Alert>
      )}
      <Stack spacing={1.75}>
        {fields.map((f) => {
          if (f.kind === "repeating_input") {
            return (
              <RepeatingBlock
                key={f.id}
                field={f}
                rows={values[f.id] || []}
                setRows={(r) => setField(f.id, r)}
                disabled={disabled}
              />
            )
          }
          return (
            <FieldRenderer
              key={f.id}
              field={f}
              value={values[f.id]}
              allValues={values}
              onChange={(v) => setField(f.id, v)}
              disabled={disabled}
              errorOverride={serverErrors[f.id]}
              formContext={payload?.context}
              onUniqueError={onUniqueError}
            />
          )
        })}
      </Stack>
      <Button
        {...primaryBtnProps}
        onClick={submit}
        disabled={disabled || hasClientError}
        sx={{ mt: 2 }}
      >
        {applyTerminology(payload?.submit_label || "Continue")}
      </Button>
    </MarkerCard>
  )
}

// ---- Multi-step form: single card, local navigation, one final submit ----
const MultiStepFormMarker: React.FC<MarkerProps> = ({
  id,
  payload,
  disabled,
  onAction,
}) => {
  const steps: any[] = payload?.steps || []
  const [currentStep, setCurrentStep] = useState(0)

  const initialValues = useMemo(() => {
    const out: Record<string, any> = {}
    steps.forEach((step: any) => {
      ;(step.fields || []).forEach((f: any) => {
        if (f.kind === "repeating_input") {
          const initRows: any[] = f.initial_rows || []
          if (!initRows.length && (f.min_rows ?? 0) > 0) {
            const blank: Record<string, any> = {}
            ;(f.row_template?.fields || []).forEach((sf: any) => {
              blank[sf.id] = sf.default ?? ""
            })
            out[f.id] = [blank]
          } else {
            out[f.id] = initRows
          }
        } else {
          out[f.id] =
            payload?.initial_values?.[f.id] ??
            f.initial_value ??
            f.default ??
            (f.kind === "multiselect" ? [] : "")
        }
      })
    })
    if (
      payload?.submitted_values &&
      typeof payload.submitted_values === "object"
    ) {
      Object.assign(out, payload.submitted_values)
    }
    return out
  }, [steps, payload?.initial_values, payload?.submitted_values])

  const [values, setValues] = useState<Record<string, any>>(initialValues)
  const serverErrors: Record<string, string> = payload?.errors || {}
  const [uniqueErrors, setUniqueErrors] = useState<Record<string, string>>({})
  const onUniqueError = useCallback((fieldId: string, message: string) => {
    setUniqueErrors((prev) => {
      if (!message && !prev[fieldId]) return prev
      if (message === prev[fieldId]) return prev
      const next = { ...prev }
      if (message) next[fieldId] = message
      else delete next[fieldId]
      return next
    })
  }, [])

  const isSubmitted =
    !!disabled &&
    !!payload?.submitted_values &&
    typeof payload.submitted_values === "object"

  useEffect(() => {
    setValues(initialValues)
    setUniqueErrors({})
    if (!isSubmitted) {
      setCurrentStep(0)
    }
  }, [id, initialValues, isSubmitted])
  useEffect(() => {
    if (
      payload?.submitted_values &&
      typeof payload.submitted_values === "object"
    ) {
      setValues((prev) => ({ ...prev, ...payload.submitted_values }))
    }
  }, [payload?.submitted_values])

  const setField = (fid: string, v: any) =>
    setValues((prev) => ({ ...prev, [fid]: v }))

  const isVisible = (field: any): boolean => {
    if (!field?.visible_when) return true
    return Object.entries(field.visible_when as Record<string, any>).every(
      ([k, v]) => values[k] === v
    )
  }

  const isStepSkipped = (step: any): boolean => {
    if (!step?.skip_when) return false
    return Object.entries(step.skip_when as Record<string, any>).every(
      ([k, v]) => values[k] === v
    )
  }

  const visibleSteps = useMemo(
    () => steps.filter((s: any) => !isStepSkipped(s)),
    [steps, values] // recalculate when training_type or other skip_when values change
  )

  const safeStep = Math.min(currentStep, Math.max(0, visibleSteps.length - 1))

  useEffect(() => {
    if (isSubmitted && visibleSteps.length > 0) {
      setCurrentStep(visibleSteps.length - 1)
    }
  }, [isSubmitted, visibleSteps.length])

  const fieldHasError = (f: any, val: any, rowValues?: Record<string, any>) => {
    if (disabled) return false
    const ctx = rowValues ?? values
    return !!(
      validateField(f, val, ctx) ||
      serverErrors[f.id] ||
      uniqueErrors[f.id] ||
      (!serverErrors[f.id] && f.helper_text)
    )
  }

  const stepHasError = (stepIdx: number): boolean => {
    const step = visibleSteps[stepIdx]
    if (!step) return false
    for (const f of step.fields || []) {
      if (!isVisible(f)) continue
      if (f.kind === "repeating_input") {
        const rows: any[] = values[f.id] || []
        if (rows.length < (f.min_rows ?? 0)) return true
        for (const row of rows) {
          for (const sub of f.row_template?.fields || []) {
            if (fieldHasError(sub, row[sub.id], row)) return true
          }
        }
      } else if (fieldHasError(f, values[f.id])) {
        return true
      }
    }
    return false
  }

  const currentStepHasError = useMemo(
    () => stepHasError(safeStep),
    [safeStep, values, visibleSteps, uniqueErrors, serverErrors]
  )

  const allStepsValid = useMemo(() => {
    for (let i = 0; i < visibleSteps.length; i++) {
      if (stepHasError(i)) return false
    }
    return true
  }, [values, visibleSteps, uniqueErrors, serverErrors]) // eslint-disable-line

  const isLastStep = safeStep === visibleSteps.length - 1
  const activeStepDef = visibleSteps[safeStep]

  return (
    <MarkerCard>
      {payload?.title && (
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          {payload.title}
        </Typography>
      )}
      <Stepper
        activeStep={safeStep}
        alternativeLabel
        className="livis-stepper livis-multistep-stepper"
        sx={{ mb: 2 }}
      >
        {visibleSteps.map((s: any, i: number) => (
          <Step key={i} completed={i < safeStep}>
            <StepLabel>{applyTerminology(s.title)}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* key forces re-mount on step change → CSS animation plays fresh */}
      <Box key={safeStep} className="livis-step-content">
        <Stack spacing={1.75}>
          {(activeStepDef?.fields || []).map((f: any) => {
            if (!isVisible(f)) return null
            if (f.kind === "repeating_input") {
              return (
                <RepeatingBlock
                  key={f.id}
                  field={f}
                  rows={values[f.id] || []}
                  setRows={(r) => setField(f.id, r)}
                  disabled={disabled}
                />
              )
            }
            return (
              <FieldRenderer
                key={f.id}
                field={f}
                value={values[f.id]}
                allValues={values}
                onChange={(v) => setField(f.id, v)}
                disabled={disabled}
                errorOverride={serverErrors[f.id]}
                formContext={payload?.context}
                onUniqueError={onUniqueError}
              />
            )
          })}
        </Stack>
      </Box>

      {Object.keys(serverErrors).length > 0 && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          Please fix the highlighted fields.
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
        {!isLastStep ? (
          <Button
            {...primaryBtnProps}
            onClick={() => setCurrentStep(safeStep + 1)}
            disabled={disabled || currentStepHasError}
          >
            Next
          </Button>
        ) : (
          <Button
            {...primaryBtnProps}
            onClick={() => onAction(id, "submit", values)}
            disabled={disabled || !allStepsValid}
          >
            {applyTerminology(payload?.submit_label || "Submit")}
          </Button>
        )}
        {safeStep > 0 && (
          <Button
            {...secondaryBtnProps}
            onClick={() => setCurrentStep(safeStep - 1)}
            disabled={disabled}
          >
            Back
          </Button>
        )}
      </Box>
    </MarkerCard>
  )
}

const UploadMarker: React.FC<MarkerProps> = ({
  id,
  payload,
  sessionId,
  disabled,
  onAction,
  onUploadComplete,
}) => {
  const [uploading, setUploading] = useState(false)
  const [done, setDone] = useState<string[]>([])
  const [dragOver, setDragOver] = useState(false)
  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || !files.length) return
      setUploading(true)
      try {
        for (const file of Array.from(files)) {
          const res = await uploadAgentFile(
            sessionId,
            payload?.kind || "golden_image",
            payload?.context || {},
            file
          )
          if (res?.file_ref) {
            setDone((d) => [...d, file.name])
            onUploadComplete?.(
              res.file_ref,
              payload?.kind || "golden_image",
              payload?.context || {}
            )
          }
        }
        if (!onUploadComplete) {
          onAction(id, "uploaded", { count: files.length })
        }
      } catch (e: any) {
        console.error(e)
      } finally {
        setUploading(false)
      }
    },
    [sessionId, payload, id, onAction, onUploadComplete]
  )
  return (
    <MarkerCard title={applyTerminology(payload?.label)}>
      <Box
        sx={{
          border: dragOver
            ? "2px dashed var(--livis-labelled-color, var(--livis-labelled-color))"
            : "2px dashed var(--livis-border-color, var(--livis-border-color))",
          borderRadius: 2,
          p: 3,
          textAlign: "center",
          background: dragOver
            ? "var(--livis-transparent-vibrant-green-extra-light-bg-color, var(--livis-transparent-vibrant-green-extra-light-bg-color))"
            : "var(--livis-color-background, var(--livis-color-background))",
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "all 0.2s",
        }}
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          handleFiles(e.dataTransfer.files)
        }}
        onClick={() =>
          !disabled && document.getElementById(`upload-${id}`)?.click()
        }
      >
        <CloudUploadIcon
          sx={{ fontSize: 36, color: "var(--livis-gray-font-color, var(--livis-gray-font-color))" }}
        />
        <Typography variant="body2" sx={{ mt: 1 }}>
          {dragOver ? "Drop files here" : "Drag & drop, or click to choose"}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "var(--livis-gray-font-color, var(--livis-gray-font-color))" }}
        >
          {payload?.accept || "image/*"}
          {payload?.multiple ? " • multiple allowed" : ""}
        </Typography>
        <input
          id={`upload-${id}`}
          type="file"
          accept={payload?.accept || "image/*"}
          multiple={!!payload?.multiple}
          disabled={disabled || uploading}
          style={{ display: "none" }}
          onChange={(e) => handleFiles(e.target.files)}
        />
      </Box>
      {uploading && <LinearProgress sx={{ mt: 1 }} />}
      {done.length > 0 && (
        <Typography
          variant="caption"
          display="block"
          sx={{ mt: 0.5, color: "var(--livis-labelled-color, var(--livis-labelled-color))" }}
        >
          ✓ {done.length} uploaded: {done.join(", ")}
        </Typography>
      )}
    </MarkerCard>
  )
}

const SummaryBody: React.FC<{ html: string }> = ({ html }) => (
  <Box className="livis-ai-prose-wrap">
    <Typography
      variant="body2"
      component="div"
      sx={{ lineHeight: 1.6 }}
      dangerouslySetInnerHTML={{ __html: renderMd(html) }}
    />
  </Box>
)

const TrainingCardMarker: React.FC<MarkerProps> = ({
  id,
  payload,
  disabled,
  onAction,
}) => (
  <MarkerCard>
    {payload?.subtitle ? (
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 1 }}
      >
        {payload.subtitle}
      </Typography>
    ) : null}
    <SummaryBody html={payload?.markdown || ""} />
    <Box
      sx={{
        mt: 1.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: "1rem",
      }}
    >
      {(payload?.buttons || []).map((b: any) => (
        <Button
          key={b.id}
          size="small"
          disabled={disabled}
          variant="outlined"
          className={
            b.variant === "primary"
              ? "livis-primary-btn"
              : "livis-secondary-btn"
          }
          onClick={() => onAction(id, b.id, null)}
          sx={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            minWidth: 100,
            maxWidth: "100%",
          }}
        >
          {b.label}
        </Button>
      ))}
    </Box>
  </MarkerCard>
)

const ConfirmMarker: React.FC<MarkerProps> = ({
  id,
  payload,
  disabled,
  onAction,
}) => (
  <MarkerCard>
    <SummaryBody html={payload?.summary_md || ""} />
    <Box
      sx={{
        mt: 1.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: "1rem",
      }}
    >
      {(
        payload?.buttons || [
          { id: "proceed", label: "Proceed", variant: "primary" },
        ]
      ).map((b: any) => (
        <Button
          key={b.id}
          size="small"
          disabled={disabled}
          variant="outlined"
          className={
            b.variant === "primary"
              ? "livis-primary-btn"
              : "livis-secondary-btn"
          }
          onClick={() => onAction(id, b.id, null)}
          sx={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            minWidth: 100,
            maxWidth: "100%",
          }}
        >
          {b.label}
        </Button>
      ))}
    </Box>
  </MarkerCard>
)

const PlanMarker: React.FC<MarkerProps> = ({
  id,
  payload,
  disabled,
  onAction,
}) => {
  const entities = payload?.entities || []
  return (
    <Card
      variant="outlined"
      sx={{
        ...cardSx,
        borderColor: "var(--livis-color-primary-blue, var(--livis-color-primary-blue))",
        borderWidth: 1.5,
      }}
      className="livis-ai-assistant-marker-card livis-plan-card"
    >
      <CardContent>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          {payload?.title}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {applyTerminology(payload?.goal)}
        </Typography>
        {entities.length > 0 && (
          <Stepper
            alternativeLabel
            activeStep={-1}
            sx={{ mb: 2 }}
            className="livis-stepper"
          >
            {entities.map((e: any, i: number) => (
              <Step key={i}>
                <StepLabel>{e.kind || e.name}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          What I will create
        </Typography>
        <Box component="ol" sx={{ pl: 2.5, mt: 0.5, mb: 1.5 }}>
          {entities.map((e: any, i: number) => (
            <li key={i} style={{ marginBottom: 6 }}>
              <Typography variant="body2">
                <b>{e.kind || e.name}</b>
                {e.name && e.kind ? ` — ${e.name}` : ""}
              </Typography>
              {e.fields && (
                <Box component="ul" sx={{ pl: 2.5, mt: 0.25, mb: 0 }}>
                  {Object.entries(e.fields).map(([k, v]: any) => (
                    <li key={k}>
                      <Typography variant="caption">
                        {k}:{" "}
                        {typeof v === "object" ? JSON.stringify(v) : String(v)}
                      </Typography>
                    </li>
                  ))}
                </Box>
              )}
            </li>
          ))}
        </Box>
        {payload?.post_flow?.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              After you confirm
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 1.5 }}>
              {payload.post_flow.map((s: string, i: number) => (
                <li key={i}>
                  <Typography variant="body2">{s}</Typography>
                </li>
              ))}
            </Box>
          </>
        )}
        <Box
          sx={{
            mt: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: "1rem",
          }}
        >
          {(payload?.buttons || []).map((b: any) => (
            <Button
              key={b.id}
              size="small"
              disabled={disabled}
              variant="outlined"
              className={
                b.variant === "primary"
                  ? "livis-primary-btn"
                  : "livis-secondary-btn"
              }
              onClick={() => onAction(id, b.id, null)}
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                minWidth: 100,
                maxWidth: "100%",
              }}
            >
              {b.label}
            </Button>
          ))}
        </Box>
      </CardContent>
    </Card>
  )
}

const ActionStatusMarker: React.FC<MarkerProps> = ({ payload }) => (
  <Box
    display="flex"
    alignItems="center"
    gap={1}
    sx={{
      my: 0.5,
      color:
        payload?.state === "failed"
          ? "var(--livis-color-secondary-red, var(--livis-color-secondary-red))"
          : "var(--livis-black-font-color, var(--livis-black-font-color))",
    }}
  >
    <StatusIcon state={payload?.state || "running"} />
    <Typography variant="body2">
      {payload?.label}
      {payload?.detail ? ` — ${payload.detail}` : ""}
    </Typography>
    {typeof payload?.progress === "number" && (
      <Box sx={{ flex: 1, ml: 1 }}>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, Math.max(0, payload.progress * 100))}
        />
      </Box>
    )}
  </Box>
)

// Render any cell value human-readably — never raw JSON. Numbers get thousands separators;
// objects become "key: value; key: value"; arrays are comma-joined; null shows as a dash.
const formatCell = (v: any): string => {
  if (v === null || v === undefined || v === "") return "—"
  if (typeof v === "number") return v.toLocaleString()
  if (typeof v === "boolean") return v ? "Yes" : "No"
  if (Array.isArray(v)) return v.map(formatCell).join(", ")
  if (typeof v === "object")
    return Object.entries(v)
      .map(([k, val]) => `${k.replace(/_/g, " ")}: ${formatCell(val)}`)
      .join("; ")
  return String(v)
}

const TableMarker: React.FC<MarkerProps> = ({ payload }) => (
  <Box sx={{ my: 1, overflowX: "auto" }} className="livis-table">
    {payload?.caption && (
      <Typography variant="caption">{payload.caption}</Typography>
    )}
    <table style={{ borderCollapse: "collapse", width: "100%" }}>
      <thead>
        <tr>
          {(payload?.columns || []).map((c: string) => (
            <th
              key={c}
              style={{
                border: `1px solid var(--livis-border-color, var(--livis-border-color))`,
                padding: "6px 10px",
                textAlign: "left",
                background: "var(--livis-color-background, var(--livis-color-background))",
                fontWeight: 600,
              }}
            >
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {(payload?.rows || []).map((r: any[], i: number) => (
          <tr key={i}>
            {r.map((cell, j) => (
              <td
                key={j}
                style={{
                  border: `1px solid var(--livis-border-color, var(--livis-border-color))`,
                  padding: "6px 10px",
                }}
              >
                {formatCell(cell)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </Box>
)

const NotificationMarker: React.FC<MarkerProps> = ({ payload }) => (
  <Alert severity={(payload?.severity || "info") as any} sx={{ my: 0 }}>
    {applyTerminology(payload?.text)}
  </Alert>
)

const ImageMarker: React.FC<MarkerProps> = ({ payload }) => (
  <MarkerCard>
    <img
      src={payload?.url}
      alt={payload?.caption || ""}
      style={{ maxWidth: payload?.width || "100%", borderRadius: 6 }}
    />
    {payload?.caption && (
      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
        {payload.caption}
      </Typography>
    )}
  </MarkerCard>
)

const GalleryMarker: React.FC<MarkerProps> = ({ payload }) => (
  <MarkerCard title={applyTerminology(payload?.title)}>
    <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 1 }}>
      {(payload?.items || []).map((it: any, i: number) => (
        <Box key={i} sx={{ flex: "0 0 auto", textAlign: "center" }}>
          <img
            src={it.url}
            alt={it.caption || ""}
            style={{ height: 80, borderRadius: 4 }}
          />
          {it.caption && (
            <Typography variant="caption" display="block">
              {it.caption}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  </MarkerCard>
)

const ReviewGalleryMarker: React.FC<MarkerProps> = ({
  id,
  payload,
  disabled,
  onAction,
}) => {
  const [flags, setFlags] = useState<
    Record<string, { flag_type: number; valid_class?: string }>
  >({})
  const setFlag = (image_id: string, flag_type: number) =>
    setFlags((prev) => ({
      ...prev,
      [image_id]: { ...(prev[image_id] || { flag_type: 0 }), flag_type },
    }))
  const submit = () => {
    const out = (payload?.items || [])
      .filter((it: any) => flags[it.image_id])
      .map((it: any) => ({
        inference_id: it.inference_id,
        image_id: it.image_id,
        flag_type: flags[it.image_id].flag_type,
      }))
    onAction(id, "submit", out)
  }
  return (
    <MarkerCard title={payload?.title || "Review predictions"}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
          gap: 1,
        }}
      >
        {(payload?.items || []).map((it: any) => (
          <Card
            key={it.image_id}
            variant="outlined"
            sx={{
              p: 1,
              textAlign: "center",
              background: "var(--livis-color-background-white, var(--livis-color-background-white))",
            }}
          >
            <img
              src={it.image_url}
              alt=""
              style={{ width: "100%", borderRadius: 4 }}
            />
            <Typography variant="caption" display="block">
              predicted: {it.predicted}
            </Typography>
            <Stack
              direction="row"
              spacing={0.5}
              justifyContent="center"
              sx={{ mt: 0.5 }}
            >
              <Button
                size="small"
                color={
                  flags[it.image_id]?.flag_type === 1 ? "success" : "inherit"
                }
                onClick={() => setFlag(it.image_id, 1)}
                disabled={disabled}
              >
                <ThumbUpIcon fontSize="small" />
              </Button>
              <Button
                size="small"
                color={
                  flags[it.image_id]?.flag_type === 0 && it.image_id in flags
                    ? "error"
                    : "inherit"
                }
                onClick={() => setFlag(it.image_id, 0)}
                disabled={disabled}
              >
                <ThumbDownIcon fontSize="small" />
              </Button>
            </Stack>
          </Card>
        ))}
      </Box>
      <Button
        {...primaryBtnProps}
        onClick={submit}
        disabled={disabled}
        sx={{ mt: 1.5 }}
      >
        {payload?.submit_label || "Submit corrections"}
      </Button>
    </MarkerCard>
  )
}

const CHART_COLORS = [
  "var(--livis-labelled-color)",
  "var(--livis-danger-red-bg-color)",
  "var(--livis-landing-header-hover-tooltip)",
  "var(--livis-recommendation-block-fix-button-bg)",
  "var(--livis-experiment-science-color)",
  "var(--livis-cad-config-add-label-color-light-green)",
  "var(--livis-recommendation-block-fix-button-bg)",
  "var(--livis-sidebar-item-default-color)",
]

const ChartMarker: React.FC<MarkerProps> = ({ payload }) => {
  const kind = payload?.kind || "bar"
  const title = payload?.title || "Chart"
  const labels: string[] = payload?.labels || []
  const series: { name: string; data: number[] }[] = payload?.series || []
  const xLabel: string = payload?.x_label || ""
  const yLabel: string = payload?.y_label || ""

  // Convert labels + series array into recharts data format
  const chartData = labels.map((label, i) => {
    // Format label if it looks like an ISO date
    let displayLabel = label
    if (
      typeof label === "string" &&
      label.endsWith("Z") &&
      label.includes("T")
    ) {
      displayLabel = convertUTCDateToLocalDate(label) || label
    } else if (
      typeof label === "string" &&
      label.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)
    ) {
      displayLabel = convertUTCDateToLocalDate(label + "Z") || label
    }

    const point: Record<string, any> = { name: displayLabel }
    series.forEach((s) => {
      point[s.name] = s.data[i] ?? 0
    })
    return point
  })

  if (kind === "pie") {
    const pieData = labels.map((label, i) => ({
      name: label,
      value: series[0]?.data[i] ?? 0,
    }))
    return (
      <MarkerCard title={applyTerminology(title)}>
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={90}
              label
            >
              {pieData.map((_, i) => (
                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </MarkerCard>
    )
  }

  if (kind === "donut") {
    const pieData = labels.map((label, i) => ({
      name: label,
      value: series[0]?.data[i] ?? 0,
    }))
    return (
      <MarkerCard title={applyTerminology(title)}>
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={90}
              label
            >
              {pieData.map((_, i) => (
                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </MarkerCard>
    )
  }

  if (kind === "line") {
    const minWidthStr = Math.max(500, chartData.length * 50) + "px"
    return (
      <MarkerCard title={applyTerminology(title)}>
        <Box sx={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
          <Box sx={{ minWidth: minWidthStr }}>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chartData}>
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  label={
                    xLabel
                      ? { value: xLabel, position: "insideBottom", offset: -4 }
                      : undefined
                  }
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  label={
                    yLabel
                      ? { value: yLabel, angle: -90, position: "insideLeft" }
                      : undefined
                  }
                />
                <Tooltip />
                <Legend />
                {series.map((s, i) => (
                  <Line
                    key={s.name}
                    type="monotone"
                    dataKey={s.name}
                    stroke={CHART_COLORS[i % CHART_COLORS.length]}
                    dot={false}
                    strokeWidth={2}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </MarkerCard>
    )
  }

  if (kind === "area") {
    return (
      <MarkerCard title={applyTerminology(title)}>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={chartData}>
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            {series.map((s, i) => (
              <Area
                key={s.name}
                type="monotone"
                dataKey={s.name}
                stroke={CHART_COLORS[i % CHART_COLORS.length]}
                fill={CHART_COLORS[i % CHART_COLORS.length] + "33"}
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </MarkerCard>
    )
  }

  if (kind === "scatter") {
    return (
      <MarkerCard title={applyTerminology(title)}>
        <ResponsiveContainer width="100%" height={240}>
          <ScatterChart>
            <XAxis dataKey="name" name="Category" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip cursor={{ strokeDasharray: "3 3" }} />
            <Legend />
            {series.map((s, i) => (
              <Scatter
                key={s.name}
                name={s.name}
                data={labels.map((lbl, idx) => ({
                  name: lbl,
                  y: s.data[idx] ?? 0,
                }))}
                fill={CHART_COLORS[i % CHART_COLORS.length]}
              />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      </MarkerCard>
    )
  }

  if (kind === "pareto") {
    const paretoData = labels
      .map((label, i) => ({
        name: label,
        value: series[0]?.data[i] ?? 0,
      }))
      .sort((a, b) => b.value - a.value) // Pareto must be sorted descending

    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisParetoHighchartsChart
          data={paretoData}
          height={300}
          columnName={series[0]?.name || "Frequency"}
          showLabels={true}
        />
      </MarkerCard>
    )
  }

  if (kind === "gauge") {
    // Gauge usually takes a single percentage value. We will take the first data point of the first series.
    const val = series[0]?.data[0] ?? 0
    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisGaugeHighcharts
          type="gauge"
          data={[{ name: "Value", value: val }]}
          value={val}
          height={240}
          title={title}
        />
      </MarkerCard>
    )
  }

  if (kind === "stacked_bar") {
    const stackedData = labels.map((label, i) => ({
      name: label,
      accepted:
        series.find((s) => s.name.toLowerCase().includes("accept"))?.data[i] ??
        series[0]?.data[i] ??
        0,
      rejected:
        series.find((s) => s.name.toLowerCase().includes("reject"))?.data[i] ??
        series[1]?.data[i] ??
        0,
      systemAccuracy: 0,
      humanAccuracy: 0,
    }))
    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisHorizontalStackedBarHighcharts data={stackedData} height={300} />
      </MarkerCard>
    )
  }

  if (kind === "heatmap") {
    // Heatmap requires a 2D array [x, y, value].
    // X is series index, Y is label index.
    const heatmapData: any[] = []
    const xCategories = series.map((s) => s.name)
    const yCategories = labels

    series.forEach((s, xIdx) => {
      s.data.forEach((val, yIdx) => {
        heatmapData.push([xIdx, yIdx, val])
      })
    })

    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisHeatmapHighcharts
          data={heatmapData}
          xAxisCategories={xCategories}
          yAxisCategories={yCategories}
          height={300}
        />
      </MarkerCard>
    )
  }

  if (kind === "composed") {
    // We pass the raw Recharts data to LivisComposedChart, which is a wrapper
    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisComposedChart
          data={chartData}
          height={300}
          barConfig={{ dataKey: series[0]?.name || "value" }}
          lineConfig={{ dataKey: series[1]?.name || "line" }}
        />
      </MarkerCard>
    )
  }

  if (kind === "accuracy") {
    const accData = labels.map((l, i) => ({
      name: l,
      humanAccuracy: series[0]?.data[i] ?? 0,
      systemAccuracy: series[1]?.data[i] ?? 0,
    }))
    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisAccuracyComparisonChart data={accData} height={300} />
      </MarkerCard>
    )
  }
  if (kind === "area_highcharts" || kind === "area") {
    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisAreaHighcharts type="area" data={chartData} height={300} />
      </MarkerCard>
    )
  }
  if (kind === "area_line") {
    const areaLineData = labels.map((l, i) => ({
      name: l,
      value: series[0]?.data[i] ?? 0,
    }))
    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisAreaLineChart data={areaLineData} height={300} />
      </MarkerCard>
    )
  }
  if (kind === "area_range") {
    const rData = labels.map((l, i) => [i, series[0]?.data[i] ?? 0]) as [
      number,
      number
    ][]
    const avgData = series[1]?.data ?? []
    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisAreaRangeChart
          rangeData={rData}
          averageData={avgData}
          height={300}
        />
      </MarkerCard>
    )
  }
  if (kind === "column") {
    const colData = labels.map((l, i) => ({
      name: l,
      y: series[0]?.data[i] ?? 0,
    }))
    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisColumnHighcharts data={colData} height={300} />
      </MarkerCard>
    )
  }
  if (kind === "donut_progress") {
    const val = series[0]?.data[0] ?? 0
    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisDonutProgressChart percentage={val} height={300} />
      </MarkerCard>
    )
  }
  if (kind === "gauge_segmented") {
    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisGaugeSegmentedChart
          type="gauge-segmented"
          data={chartData}
          height={300}
        />
      </MarkerCard>
    )
  }
  if (kind === "gradient_bar") {
    const gradData = series[0]?.data ?? []
    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisGradientBarChart
          categories={labels}
          data={gradData}
          height={300}
        />
      </MarkerCard>
    )
  }
  if (kind === "horizontal_bar") {
    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisHorizontalBarHighcharts
          type="horizontal-bar"
          data={chartData}
          height={300}
        />
      </MarkerCard>
    )
  }
  if (kind === "vertical_bar") {
    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisBarVerticalChart
          data={chartData}
          barKeys={[
            { key: series[0]?.name || "value", color: CHART_COLORS[0] },
          ]}
          height={300}
        />
      </MarkerCard>
    )
  }
  if (kind === "pie_donut") {
    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisPieDonutChart type="pie" data={chartData} height={300} />
      </MarkerCard>
    )
  }
  if (kind === "pill_bar") {
    const pillData = labels.map((l, i) => ({
      name: l,
      value: series[0]?.data[i] ?? 0,
    }))
    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisPillBarChart data={pillData} height={300} />
      </MarkerCard>
    )
  }
  if (kind === "progress") {
    const progData = labels.map((l, i) => ({
      name: l,
      value: series[0]?.data[i] ?? 0,
    }))
    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisProgressHighcharts type="progress" data={progData} height={300} />
      </MarkerCard>
    )
  }
  if (kind === "spline") {
    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisSplineChart type="spline" data={chartData} height={300} />
      </MarkerCard>
    )
  }
  if (kind === "vertical_stacked_bar") {
    const stackedData = labels.map((label, i) => ({
      name: label,
      accepted:
        series.find((s) => s.name.toLowerCase().includes("accept"))?.data[i] ??
        series[0]?.data[i] ??
        0,
      rejected:
        series.find((s) => s.name.toLowerCase().includes("reject"))?.data[i] ??
        series[1]?.data[i] ??
        0,
    }))
    return (
      <MarkerCard title={applyTerminology(title)}>
        <LivisStackedBarHighcharts data={stackedData as any} height={300} />
      </MarkerCard>
    )
  }

  // Default: bar
  const minWidthStr = Math.max(500, chartData.length * 50) + "px"
  return (
    <MarkerCard title={applyTerminology(title)}>
      <Box sx={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
        <Box sx={{ minWidth: minWidthStr }}>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              {series.map((s, i) => (
                <Bar
                  key={s.name}
                  dataKey={s.name}
                  fill={CHART_COLORS[i % CHART_COLORS.length]}
                  radius={[3, 3, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </MarkerCard>
  )
}

const MatrixMarker: React.FC<MarkerProps> = ({ payload }) => {
  const labels = payload?.labels || []
  const values = payload?.values || []
  return (
    <MarkerCard title={payload?.title || "Confusion matrix"}>
      <Box sx={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th></th>
              {labels.map((l: string) => (
                <th
                  key={l}
                  style={{
                    padding: "4px 8px",
                    borderBottom:
                      "1px solid var(--livis-border-color, var(--livis-border-color))",
                  }}
                >
                  {l}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {values.map((row: number[], i: number) => (
              <tr key={i}>
                <th
                  style={{
                    padding: "4px 8px",
                    borderRight: "1px solid var(--livis-border-color, var(--livis-border-color))",
                  }}
                >
                  {labels[i]}
                </th>
                {row.map((v, j) => {
                  const isDiag = i === j
                  return (
                    <td
                      key={j}
                      style={{
                        padding: "4px 8px",
                        textAlign: "center",
                        background: isDiag
                          ? "var(--livis-transparent-vibrant-green-extra-light-bg-color, var(--livis-transparent-vibrant-green-extra-light-bg-color))"
                          : "var(--livis-inspection-defect-reason-bg-color, var(--livis-inspection-status-rejected-border-color))",
                      }}
                    >
                      {v}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </MarkerCard>
  )
}

const RepeatingInputMarker: React.FC<MarkerProps> = ({
  id,
  payload,
  disabled,
  onAction,
}) => {
  const tmpl = payload?.field_template || { kind: "text", label: "Value" }
  const [rows, setRows] = useState<string[]>([""])
  const max = payload?.max || 8
  const min = payload?.min || 1
  const submit = () =>
    onAction(
      id,
      "submit",
      rows.filter((r) => r.trim())
    )
  return (
    <MarkerCard title={payload?.title || tmpl.label}>
      <Stack spacing={1}>
        {rows.map((r, i) => (
          <Box key={i} sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            <TextField
              size="small"
              className={textFieldClass}
              fullWidth
              type={tmpl.kind === "number" ? "number" : "text"}
              placeholder={tmpl.placeholder || ""}
              value={r}
              disabled={disabled}
              onChange={(e) =>
                setRows((rs) =>
                  rs.map((x, j) => (j === i ? e.target.value : x))
                )
              }
              variant="standard"
            />
            {rows.length > min && (
              <Button
                size="small"
                onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                disabled={disabled}
              >
                <RemoveIcon fontSize="small" />
              </Button>
            )}
          </Box>
        ))}
        {rows.length < max && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setRows((rs) => [...rs, ""])}
            disabled={disabled}
          >
            {addButtonLabel(payload?.add_label, "add another")}
          </Button>
        )}
      </Stack>
      <Button
        {...primaryBtnProps}
        onClick={submit}
        disabled={disabled}
        sx={{ mt: 1.5 }}
      >
        Submit
      </Button>
    </MarkerCard>
  )
}

const OptionSelectMarker: React.FC<MarkerProps> = ({
  id,
  payload,
  disabled,
  onAction,
}) => {
  const { prompt, options } = payload || {}
  const selectedId = payload?.selected_option_id as string | undefined
  return (
    <MarkerCard>
      {prompt && (
        <Typography
          variant="body2"
          sx={{ mb: 1.5, fontWeight: 500 }}
          dangerouslySetInnerHTML={{ __html: renderMd(prompt) }}
        />
      )}
      {disabled && selectedId && payload?.selected_option_label ? (
        <Typography variant="body2" sx={{ mb: 1, color: "var(--chat-text-2)" }}>
          Selected: {applyTerminology(String(payload.selected_option_label))}
        </Typography>
      ) : null}
      <Stack spacing={0.75}>
        {(options || []).map((o: any) => {
          const optId = o.id || o.value || o.label || ""
          const isSelected = disabled && selectedId === optId
          return (
            <Button
              key={optId}
              variant={isSelected ? "contained" : "outlined"}
              className={isSelected ? undefined : "livis-secondary-btn"}
              size="small"
              disabled={disabled}
              onClick={() => onAction(id, optId, null)}
              sx={{
                justifyContent: "flex-start",
                textAlign: "left",
                minHeight: 40,
                py: 0.75,
                ...(isSelected
                  ? {
                      bgcolor: "var(--livis-color-primary-blue, var(--livis-color-primary-blue))",
                      color: "var(--livis-white-color)",
                      "&.Mui-disabled": {
                        bgcolor: "var(--livis-color-primary-blue, var(--livis-color-primary-blue))",
                        color: "var(--livis-white-color)",
                        opacity: 0.85,
                      },
                    }
                  : {}),
              }}
              fullWidth
            >
              <Box sx={{ textAlign: "left" }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ lineHeight: 1.3 }}
                >
                  {applyTerminology(o.label)}
                </Typography>
                {o.description && (
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{
                      mt: 0.25,
                      color: "var(--livis-gray-font-color, var(--livis-gray-font-color))",
                    }}
                  >
                    {applyTerminology(o.description)}
                  </Typography>
                )}
              </Box>
            </Button>
          )
        })}
      </Stack>
    </MarkerCard>
  )
}

const AgentPlanReviewMarker: React.FC<MarkerProps> = ({
  id,
  payload,
  disabled,
  onAction,
}) => {
  const { title, summary, fields, buttons } = payload || {}
  return (
    <Card
      variant="outlined"
      sx={{
        ...cardSx,
        borderColor: "var(--livis-color-primary-blue, var(--livis-color-primary-blue))",
        borderLeftWidth: 3,
      }}
      className="livis-ai-assistant-marker-card livis-plan-review-card"
    >
      <CardContent>
        {title && (
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ mb: 0.5, color: "var(--livis-color-primary-blue, var(--livis-color-primary-blue))" }}
          >
            {title}
          </Typography>
        )}
        {summary && (
          <Typography
            variant="body2"
            sx={{ mb: 1.5, color: "var(--livis-gray-font-color, var(--livis-gray-font-color))" }}
            dangerouslySetInnerHTML={{ __html: renderMd(summary) }}
          />
        )}
        {(fields || []).length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            {(fields as any[]).map((f: any, i: number) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  gap: 2,
                  mb: 0.5,
                  alignItems: "flex-start",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    minWidth: 100,
                    flexShrink: 0,
                    color: "var(--livis-gray-font-color, var(--livis-gray-font-color))",
                  }}
                >
                  {f.label}
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {f.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        {(buttons || []).length > 0 && (
          <Box
            sx={{
              mt: 1.5,
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: "1rem",
            }}
          >
            {(buttons as any[]).map((b: any) => (
              <Button
                key={b.id}
                size="small"
                disabled={disabled}
                variant="outlined"
                className={
                  b.variant === "primary"
                    ? "livis-primary-btn"
                    : "livis-secondary-btn"
                }
                onClick={() => onAction(id, b.id, null)}
              >
                {b.label}
              </Button>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

const NavigateMarker: React.FC<MarkerProps> = ({ id, payload, onAction }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const callSideBarApi = useSelector((state: any) => state.sidebarAPIReducer)
  const {
    label,
    description,
    route,
    button_label,
    session_storage,
    or_actions,
  } = payload || {}

  const handleNavigate = () => {
    if (!route) return
    if (session_storage && typeof session_storage === "object") {
      const ss = session_storage as Record<string, unknown>
      if (ss.station_data && typeof ss.station_data === "object") {
        setWorkstationType(ss.station_data)
      }
      if (ss.usecase_data && typeof ss.usecase_data === "object") {
        setSelectedUsecase(ss.usecase_data)
      }
      if (ss.part_data && typeof ss.part_data === "object") {
        const partData = ss.part_data as Record<string, unknown>
        setLocalSelectedPart(ss.part_data)
        if (partData.part_name) {
          dispatch(setSelectedPart(String(partData.part_name)))
        }
      }
      Object.entries(ss).forEach(([key, value]) => {
        if (["station_data", "usecase_data", "part_data"].includes(key)) return
        if (key === "manage_data_tab" && typeof value === "string") {
          setDataTab(value)
          return
        }
        sessionStorage.setItem(
          key,
          typeof value === "string" ? value : JSON.stringify(value)
        )
      })
    }
    sessionStorage.removeItem("hasReloaded")
    window.dispatchEvent(
      new CustomEvent("livis_agent_refresh", {
        detail: {
          resources: [
            "data",
            "images",
            "parts",
            "usecases",
            "workstations",
            "training_status",
          ],
        },
      })
    )
    dispatch(setCallSidebarAPI(!callSideBarApi))
    navigate(route)
  }

  return (
    <MarkerCard>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <OpenInNewIcon
          sx={{ color: "primary.main", mt: 0.25, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1 }}>
          {label && (
            <Typography variant="subtitle2" fontWeight={600}>
              {label}
            </Typography>
          )}
          {description && (
            <Typography
              variant="body2"
              sx={{ mb: 1, color: "var(--livis-gray-font-color, var(--livis-gray-font-color))" }}
              dangerouslySetInnerHTML={{ __html: renderMd(description) }}
            />
          )}
          <Button
            {...primaryBtnProps}
            className="livis-primary-btn"
            endIcon={<OpenInNewIcon fontSize="small" />}
            onClick={handleNavigate}
            sx={{ mt: 0.5 }}
          >
            {button_label || "Open →"}
          </Button>
          {or_actions && or_actions.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              {or_actions.map((a: { id: string; label: string }) => (
                <Box key={a.id}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      my: 1,
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        height: "1px",
                        background: "var(--livis-border-color, var(--livis-border-color))",
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "var(--livis-gray-font-color, var(--livis-gray-font-color))",
                        fontWeight: 500,
                      }}
                    >
                      or
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        height: "1px",
                        background: "var(--livis-border-color, var(--livis-border-color))",
                      }}
                    />
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    onClick={() => onAction && onAction(id, a.id, null)}
                    sx={{
                      textTransform: "none",
                      borderColor: "var(--livis-color-primary-blue, var(--livis-color-primary-blue))",
                      color: "var(--livis-color-primary-blue, var(--livis-color-primary-blue))",
                      fontSize: "0.8125rem",
                      "&:hover": {
                        background:
                          "var(--livis-color-primary-blue-light, var(--livis-deep-blue-ultralight-bg-color))",
                        borderColor: "var(--livis-color-primary-blue, var(--livis-color-primary-blue))",
                      },
                    }}
                  >
                    {a.label}
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </MarkerCard>
  )
}

const SourcesMarker: React.FC<MarkerProps> = ({ payload }) => {
  const items: Array<{ title?: string; url?: string; snippet?: string }> =
    payload?.items || []
  if (!items.length) return null
  return (
    <Box sx={{ mt: 1, mb: 0.5 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.5 }}
      >
        Sources
      </Typography>
      <Stack spacing={0.5}>
        {items.map((s, i) => (
          <Typography key={i} variant="caption" component="div">
            {s.url ? (
              <a href={s.url} target="_blank" rel="noopener noreferrer">
                {s.title || s.url}
              </a>
            ) : (
              s.title || "Source"
            )}
            {s.snippet ? ` — ${s.snippet}` : ""}
          </Typography>
        ))}
      </Stack>
    </Box>
  )
}

const QuickRepliesMarker: React.FC<MarkerProps> = ({
  id,
  payload,
  disabled,
  onAction,
}) => {
  const [picked, setPicked] = useState(false)
  const replies: Array<{ id: string; label: string }> = payload?.replies || []
  if (picked || replies.length === 0) return null

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        flexWrap: "wrap",
        mt: 1,
        mb: 0.5,
        animation: "fadeInUp 0.3s ease forwards",
      }}
    >
      {replies.map((r) => (
        <Chip
          key={r.id}
          label={r.label}
          aria-label={r.label}
          onClick={() => {
            setPicked(true)
            onAction(id, "quick_reply", { id: r.id, label: r.label })
          }}
          disabled={disabled}
          variant="outlined"
          size="small"
          sx={{
            cursor: "pointer",
            borderColor: "var(--livis-color-primary-blue, var(--livis-color-primary-blue))",
            color: "var(--livis-color-primary-blue, var(--livis-color-primary-blue))",
            borderRadius: "20px",
            fontWeight: 500,
            fontSize: "0.78rem",
            transition: "all 0.15s ease",
            "&:hover": {
              background: "var(--livis-deep-blue-ultralight-bg-color)",
              transform: "translateY(-1px)",
              
            },
            "&.Mui-disabled": { opacity: 0.4 },
          }}
        />
      ))}
    </Box>
  )
}

const ActionMenuMarker: React.FC<MarkerProps> = ({
  id,
  payload,
  disabled,
  onAction,
}) => {
  const [picked, setPicked] = useState(false)
  const actions: Array<{
    id: string
    label: string
    description?: string
    icon?: string
  }> = payload?.actions || []
  if (picked || actions.length === 0) return null

  return (
    <Box
      sx={{
        mt: 1.5,
        mb: 0.5,
        borderRadius: "10px",
        overflow: "hidden",
        border: "1px solid var(--chat-border, var(--livis-transparent-black-soft-bg-color))",
        animation: "fadeInUp 0.3s ease forwards",
      }}
    >
      {actions.map((a, idx) => (
        <Box
          key={a.id}
          // C4: action rows are clickable divs — give them button role + keyboard support,
          // and an optimistic hide (setPicked) so the tap feels instant.
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={a.label}
          aria-disabled={disabled || undefined}
          onClick={() => {
            if (disabled) return
            setPicked(true)
            onAction(id, "quick_reply", { id: a.id, label: a.label })
          }}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (disabled) return
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              setPicked(true)
              onAction(id, "quick_reply", { id: a.id, label: a.label })
            }
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1.4,
            cursor: disabled ? "default" : "pointer",
            borderTop:
              idx > 0
                ? "1px solid var(--chat-border, var(--livis-black-low-opacity-bg-color))"
                : "none",
            background: "var(--chat-bg, var(--livis-white-color))",
            transition: "background 0.15s ease",
            opacity: disabled ? 0.5 : 1,
            "&:hover": disabled
              ? {}
              : { background: "var(--chat-hover, var(--livis-deep-blue-ultralight-bg-color))" },
          }}
        >
          {a.icon && (
            <Box sx={{ fontSize: "1.25rem", flexShrink: 0, lineHeight: 1 }}>
              {a.icon}
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                fontWeight: 600,
                fontSize: "0.875rem",
                color: "var(--chat-text-1)",
                lineHeight: 1.3,
              }}
            >
              {a.label}
            </Box>
            {a.description && (
              <Box
                sx={{
                  fontSize: "0.75rem",
                  color: "var(--chat-text-2)",
                  mt: 0.2,
                  lineHeight: 1.4,
                }}
              >
                {a.description}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              color: "var(--chat-text-2)",
              fontSize: "1rem",
              flexShrink: 0,
            }}
          >
            ›
          </Box>
        </Box>
      ))}
    </Box>
  )
}

const AgentMarkerInner: React.FC<MarkerProps> = (props) => {
  const t = props?.payload?.marker_type
  switch (t) {
    case "choose":
      return <ChooseMarker {...props} />
    case "input":
      return <InputMarker {...props} />
    case "form":
      return <FormMarker {...props} />
    case "multi_step_form":
      return <MultiStepFormMarker {...props} />
    case "upload_request":
      return <UploadMarker {...props} />
    case "training_card":
      return <TrainingCardMarker {...props} />
    case "confirm":
      return <ConfirmMarker {...props} />
    case "plan":
      return <PlanMarker {...props} />
    case "action_status":
      return <ActionStatusMarker {...props} />
    case "table":
      return <TableMarker {...props} />
    case "notification":
      return <NotificationMarker {...props} />
    case "image":
      return <ImageMarker {...props} />
    case "gallery":
      return <GalleryMarker {...props} />
    case "review_gallery":
      return <ReviewGalleryMarker {...props} />
    case "chart":
      return <ChartMarker {...props} />
    case "matrix":
      return <MatrixMarker {...props} />
    case "repeating_input":
      return <RepeatingInputMarker {...props} />
    case "navigate":
      return <NavigateMarker {...props} />
    case "option_select":
      return <OptionSelectMarker {...props} />
    case "agent_plan_review":
      return <AgentPlanReviewMarker {...props} />
    case "sources":
      return <SourcesMarker {...props} />
    case "quick_replies":
      return <QuickRepliesMarker {...props} />
    case "action_menu":
      return <ActionMenuMarker {...props} />
    default:
      return null
  }
}

// Memoized — without this the marker tree re-renders on every keystroke when
// the chat input lives anywhere up the tree (severe lag on rich forms).
export const AgentMarker = React.memo(
  AgentMarkerInner,
  (prev, next) =>
    prev.id === next.id &&
    prev.disabled === next.disabled &&
    prev.sessionId === next.sessionId &&
    prev.payload === next.payload &&
    prev.onAction === next.onAction &&
    prev.onUploadComplete === next.onUploadComplete
)
